// Platform console → Feedback: every hospital's staff feedback in one inbox
// (routes/platform/feedback.js). Triage a piece of feedback (status,
// priority, internal notes, a reply the submitter sees under "My feedback"),
// listen to its voice note, view its screenshot, and convert it into a
// product requirement — new, or linked to one that already exists so
// several hospitals asking for the same thing land on one requirement.
import { useEffect, useMemo, useState } from 'react';
import { platformApi, fetchPlatformBlob } from '../lib/platformApi/client';
import type {
  FeedbackCategory, FeedbackPage, FeedbackStatus, PlatformTenant, Priority, ProductRequirement, StaffFeedback,
} from './types';

const CATEGORY: Record<FeedbackCategory, { label: string; icon: string }> = {
  bug: { label: 'Problem', icon: '🐞' },
  idea: { label: 'Suggestion', icon: '💡' },
  question: { label: 'Question', icon: '❓' },
  praise: { label: 'Praise', icon: '👍' },
  other: { label: 'Other', icon: '💬' },
};

export const FEEDBACK_STATUS: Record<FeedbackStatus, { label: string; bg: string; color: string }> = {
  new:       { label: 'New',       bg: '#DBEAFE', color: '#1E40AF' },
  reviewing: { label: 'Reviewing', bg: '#FEF3C7', color: '#92400E' },
  converted: { label: 'Converted', bg: '#EDE9FE', color: '#5B21B6' },
  resolved:  { label: 'Resolved',  bg: '#DCFCE7', color: '#166534' },
  dismissed: { label: 'Dismissed', bg: '#F3F4F6', color: '#6B7280' },
};

const PRIORITIES: Priority[] = ['low', 'medium', 'high', 'critical'];
const FILTERS: { id: string; label: string }[] = [
  { id: 'open', label: 'Open' }, { id: 'new', label: 'New' }, { id: 'reviewing', label: 'Reviewing' },
  { id: 'converted', label: 'Converted' }, { id: 'resolved', label: 'Resolved' }, { id: 'dismissed', label: 'Dismissed' },
  { id: '', label: 'All' },
];

const ghost = { background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' } as const;
const pill = (bg: string, color: string) => ({ background: bg, color, borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' as const });
const errorBox = { padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 13, marginBottom: 12 } as const;

function message(e: unknown) { return e instanceof Error ? e.message : String(e); }

/** Voice-note parts fetched with the operator token, played one after another. */
function VoiceNote({ feedbackId, parts, seconds }: { feedbackId: number; parts: number; seconds: number | null }) {
  const [urls, setUrls] = useState<string[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    const made: string[] = [];
    (async () => {
      try {
        for (let i = 0; i < parts; i++) {
          const blob = await fetchPlatformBlob(`/feedback/${feedbackId}/audio/${i}`);
          made.push(URL.createObjectURL(blob));
        }
        if (!cancelled) setUrls([...made]);
      } catch (e) { if (!cancelled) setError(message(e)); }
    })();
    return () => { cancelled = true; made.forEach(u => URL.revokeObjectURL(u)); };
  }, [feedbackId, parts]);

  if (error) return <div style={{ fontSize: 12, color: '#991B1B' }}>Voice note unavailable: {error}</div>;
  if (!urls.length) return <div style={{ fontSize: 12, color: '#9CA3AF' }}>Loading voice note…</div>;
  return (
    <div>
      <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
        Voice note{seconds ? ` · ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : ''}{urls.length > 1 ? ` · ${urls.length} parts, played in order` : ''}
      </div>
      {urls.map((u, i) => (
        <audio
          key={u} src={u} controls preload="metadata" id={`fb-${feedbackId}-part-${i}`}
          style={{ width: '100%', height: 36, marginBottom: 4 }}
          onEnded={() => (document.getElementById(`fb-${feedbackId}-part-${i + 1}`) as HTMLAudioElement | null)?.play()}
        />
      ))}
    </div>
  );
}

function Attachment({ feedback }: { feedback: StaffFeedback }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let made = '';
    fetchPlatformBlob(`/feedback/${feedback.id}/attachment`)
      .then(b => { made = URL.createObjectURL(b); setUrl(made); })
      .catch(e => setError(message(e)));
    return () => { if (made) URL.revokeObjectURL(made); };
  }, [feedback.id]);
  if (error) return <div style={{ fontSize: 12, color: '#991B1B' }}>Attachment unavailable: {error}</div>;
  if (!url) return <div style={{ fontSize: 12, color: '#9CA3AF' }}>Loading attachment…</div>;
  if (feedback.attachment_mime?.startsWith('image/')) {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={feedback.attachment_name || 'Screenshot'} style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 8, border: '1px solid #E5E7EB' }} />
      </a>
    );
  }
  return <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>📎 {feedback.attachment_name || 'Open attachment'}</a>;
}

function buildRequirementDescription(f: StaffFeedback) {
  return [
    f.message,
    f.transcript && f.transcript !== f.message ? `Voice transcript: ${f.transcript}` : null,
    '',
    `— From ${f.ref}: ${f.user_name || 'staff'}${f.user_role ? ` (${f.user_role})` : ''}, ${f.tenant_name}${f.branch_name ? ` / ${f.branch_name}` : ''}${f.module ? `, on ${f.module}` : ''}.`,
  ].filter(v => v !== null).join('\n').trim();
}

function ConvertPanel({ feedback, onDone }: { feedback: StaffFeedback; onDone: (f: StaffFeedback) => void }) {
  const [mode, setMode] = useState<'new' | 'link'>('new');
  const [title, setTitle] = useState(feedback.title || (feedback.message || '').slice(0, 120));
  const [description, setDescription] = useState(buildRequirementDescription(feedback));
  const [priority, setPriority] = useState<Priority>(feedback.priority || 'medium');
  const [requirements, setRequirements] = useState<ProductRequirement[]>([]);
  const [linkId, setLinkId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (mode === 'link' && !requirements.length) {
      platformApi.get<ProductRequirement[]>('/requirements?status=open').then(setRequirements).catch(e => setError(message(e)));
    }
  }, [mode, requirements.length]);

  const submit = async () => {
    setSaving(true); setError('');
    try {
      const body = mode === 'link' ? { requirement_id: Number(linkId) } : { title, description, priority, module: feedback.module };
      onDone(await platformApi.post<StaffFeedback>(`/feedback/${feedback.id}/convert`, body));
    } catch (e) { setError(message(e)); }
    setSaving(false);
  };

  return (
    <div style={{ border: '1px solid #DDD6FE', background: '#F5F3FF', borderRadius: 10, padding: 14, marginTop: 12 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button style={{ ...ghost, fontWeight: mode === 'new' ? 700 : 400 }} onClick={() => setMode('new')}>New requirement</button>
        <button style={{ ...ghost, fontWeight: mode === 'link' ? 700 : 400 }} onClick={() => setMode('link')}>Add to an existing one</button>
      </div>
      {mode === 'new' ? (
        <>
          <label className="lbl">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
          <label className="lbl" style={{ marginTop: 8 }}>Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={6} />
          <label className="lbl" style={{ marginTop: 8 }}>Priority</label>
          <select value={priority} onChange={e => setPriority(e.target.value as Priority)}>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </>
      ) : (
        <>
          <label className="lbl">Requirement</label>
          <select value={linkId} onChange={e => setLinkId(e.target.value)}>
            <option value="">Choose…</option>
            {requirements.map(r => <option key={r.id} value={r.id}>{r.ref} — {r.title} ({r.feedback_count} linked)</option>)}
          </select>
        </>
      )}
      {error && <div style={{ ...errorBox, marginTop: 10 }}>{error}</div>}
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn-primary" disabled={saving || (mode === 'new' ? !title.trim() : !linkId)} onClick={submit}>
          {saving ? 'Saving…' : mode === 'new' ? 'Create requirement' : 'Link feedback'}
        </button>
      </div>
    </div>
  );
}

function FeedbackDetail({ feedback, onChanged }: { feedback: StaffFeedback; onChanged: (f: StaffFeedback) => void }) {
  const [status, setStatus] = useState<FeedbackStatus>(feedback.status);
  const [priority, setPriority] = useState<Priority | ''>(feedback.priority || '');
  const [notes, setNotes] = useState(feedback.operator_notes || '');
  const [reply, setReply] = useState(feedback.reply_to_user || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    setStatus(feedback.status); setPriority(feedback.priority || ''); setNotes(feedback.operator_notes || '');
    setReply(feedback.reply_to_user || ''); setConverting(false); setError('');
  }, [feedback]);

  const dirty = status !== feedback.status || (priority || null) !== feedback.priority
    || notes !== (feedback.operator_notes || '') || reply !== (feedback.reply_to_user || '');

  const save = async () => {
    setSaving(true); setError('');
    try {
      onChanged(await platformApi.patch<StaffFeedback>(`/feedback/${feedback.id}`, {
        status, priority: priority || null, operator_notes: notes, reply_to_user: reply,
      }));
    } catch (e) { setError(message(e)); }
    setSaving(false);
  };

  const c = CATEGORY[feedback.category];
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: '#6B7280' }}>{feedback.ref}</span>
        <span style={pill(FEEDBACK_STATUS[feedback.status].bg, FEEDBACK_STATUS[feedback.status].color)}>{FEEDBACK_STATUS[feedback.status].label}</span>
        <span style={{ fontSize: 12, color: '#6B7280' }}>{c.icon} {c.label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6B7280' }}>{new Date(feedback.created_at).toLocaleString()}</span>
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, margin: '6px 0' }}>{feedback.title || (feedback.message ? feedback.message.slice(0, 90) : 'Voice feedback')}</div>
      <div style={{ fontSize: 12, color: '#374151', marginBottom: 12 }}>
        <strong>{feedback.user_name || feedback.username || 'Staff'}</strong>
        {feedback.user_role && <> · {feedback.user_role}</>} · {feedback.tenant_name}
        {feedback.branch_name && <> / {feedback.branch_name}</>}
        {feedback.module && <> · on <strong>{feedback.module}</strong></>}
      </div>

      {feedback.message && <div style={{ fontSize: 13.5, whiteSpace: 'pre-wrap', lineHeight: 1.55, marginBottom: 12 }}>{feedback.message}</div>}
      {feedback.transcript && feedback.transcript !== feedback.message && (
        <div style={{ fontSize: 12.5, background: '#F9FAFB', borderRadius: 8, padding: 10, marginBottom: 12, whiteSpace: 'pre-wrap' }}>
          <span style={{ color: '#6B7280' }}>Transcript: </span>{feedback.transcript}
        </div>
      )}
      {feedback.audio_parts > 0 && <div style={{ marginBottom: 12 }}><VoiceNote feedbackId={feedback.id} parts={feedback.audio_parts} seconds={feedback.audio_seconds} /></div>}
      {feedback.has_attachment && <div style={{ marginBottom: 12 }}><Attachment feedback={feedback} /></div>}

      <details style={{ fontSize: 11.5, color: '#6B7280', marginBottom: 14 }}>
        <summary style={{ cursor: 'pointer' }}>Technical details</summary>
        <div style={{ marginTop: 4, lineHeight: 1.6 }}>
          Page: <code>{feedback.page_url || '—'}</code><br />
          Screen: {feedback.screen || '—'}<br />
          Browser: {feedback.user_agent || '—'}<br />
          User: {feedback.username || '—'} (id {feedback.user_id ?? '—'}), branch id {feedback.branch_id ?? '—'}
        </div>
      </details>

      <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label className="lbl">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value as FeedbackStatus)}>
              {(Object.keys(FEEDBACK_STATUS) as FeedbackStatus[]).map(s => <option key={s} value={s}>{FEEDBACK_STATUS[s].label}</option>)}
            </select>
          </div>
          <div>
            <label className="lbl">Priority</label>
            <select value={priority} onChange={e => setPriority(e.target.value as Priority | '')}>
              <option value="">Not set</option>
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <label className="lbl" style={{ marginTop: 10 }}>Internal notes <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(only the ClintoBytes team sees these)</span></label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
        <label className="lbl" style={{ marginTop: 10 }}>Reply to {feedback.user_name || 'the submitter'} <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(shown under My feedback)</span></label>
        <textarea value={reply} onChange={e => setReply(e.target.value)} rows={2} placeholder="e.g. Thanks — this is fixed in the next update." />
        {error && <div style={{ ...errorBox, marginTop: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
          {feedback.requirement_ref ? (
            <span style={{ fontSize: 12.5 }}>
              📋 <strong>{feedback.requirement_ref}</strong> — {feedback.requirement_title} <span style={{ color: '#6B7280' }}>({feedback.requirement_status?.replace('_', ' ')})</span>
            </span>
          ) : (
            <button style={ghost} onClick={() => setConverting(v => !v)}>{converting ? 'Cancel' : '📋 Convert to requirement'}</button>
          )}
          <button className="btn-primary" style={{ marginLeft: 'auto' }} disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
        {converting && !feedback.requirement_ref && <ConvertPanel feedback={feedback} onDone={(f) => { setConverting(false); onChanged(f); }} />}
      </div>
    </div>
  );
}

export default function FeedbackInboxScreen({ onBack, onOpenRequirements }: { onBack: () => void; onOpenRequirements: () => void }) {
  const [filter, setFilter] = useState('open');
  const [tenantId, setTenantId] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<FeedbackPage | null>(null);
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => { platformApi.get<PlatformTenant[]>('/tenants').then(setTenants).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => { setSearch(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);

  const query = useMemo(() => {
    const p = new URLSearchParams({ page: String(page), limit: '50' });
    if (filter) p.set('status', filter);
    if (tenantId) p.set('tenant_id', tenantId);
    if (category) p.set('category', category);
    if (search.trim()) p.set('q', search.trim());
    return p.toString();
  }, [filter, tenantId, category, search, page]);

  const load = () => {
    platformApi.get<FeedbackPage>(`/feedback?${query}`)
      .then(d => { setData(d); setError(''); })
      .catch(e => setError(message(e)));
  };
  useEffect(load, [query]);

  const selected = data?.data.find(f => f.id === selectedId) ?? null;
  const replace = (f: StaffFeedback) => setData(d => (d ? { ...d, data: d.data.map(x => (x.id === f.id ? f : x)) } : d));

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto', padding: '24px 20px' }}>
      <button onClick={onBack} style={{ marginBottom: 12, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', padding: 0 }}>← Back to tenants</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>Staff feedback</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Sent from the Feedback tab in every hospital's app.</div>
        </div>
        <button style={ghost} onClick={onOpenRequirements}>📋 Requirements</button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => { setFilter(f.id); setPage(1); }}
            style={{ ...ghost, padding: '4px 12px', fontSize: 12, background: filter === f.id ? '#111827' : 'var(--bg)', color: filter === f.id ? '#fff' : undefined }}>
            {f.label}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={tenantId} onChange={e => { setTenantId(e.target.value); setPage(1); }} style={{ width: 'auto', minWidth: 180 }}>
          <option value="">All hospitals</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }} style={{ width: 'auto', minWidth: 150 }}>
          <option value="">All types</option>
          {(Object.keys(CATEGORY) as FeedbackCategory[]).map(c => <option key={c} value={c}>{CATEGORY[c].icon} {CATEGORY[c].label}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search text, ref, person, screen…" style={{ flex: 1, minWidth: 220 }} />
      </div>

      {error && <div style={errorBox}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: 16, alignItems: 'start' }}>
        <div className="card" style={{ overflow: 'hidden' }}>
          {!data && <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>}
          {data && data.data.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>Nothing here.</div>}
          {data?.data.map(f => {
            const s = FEEDBACK_STATUS[f.status];
            return (
              <button key={f.id} onClick={() => setSelectedId(f.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', borderBottom: '1px solid #F3F4F6',
                  background: f.id === selectedId ? '#F0FDFA' : 'transparent', cursor: 'pointer',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13 }}>{CATEGORY[f.category].icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.title || f.message || 'Voice feedback'}
                  </span>
                  <span style={pill(s.bg, s.color)}>{s.label}</span>
                </div>
                <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 3 }}>
                  {f.ref} · {f.tenant_name} · {f.user_name || 'Staff'}{f.user_role ? ` (${f.user_role})` : ''} · {new Date(f.created_at).toLocaleDateString()}
                  {f.audio_parts > 0 && ' · 🎙'}{f.has_attachment && ' · 📎'}{f.priority && ` · ${f.priority}`}
                </div>
              </button>
            );
          })}
          {data && data.total > data.limit && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', fontSize: 12, color: '#6B7280' }}>
              <button style={ghost} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
              Page {data.page} · {data.total} total
              <button style={ghost} disabled={!data.has_more} onClick={() => setPage(p => p + 1)}>Next ›</button>
            </div>
          )}
        </div>
        <div>
          {selected
            ? <FeedbackDetail feedback={selected} onChanged={replace} />
            : <div className="card" style={{ padding: 30, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Select feedback to read it.</div>}
        </div>
      </div>
    </div>
  );
}
